import { CellClickedEvent, ValueFormatterParams } from "ag-grid-community";
import { ICellRendererParams } from "ag-grid-enterprise";
import { CustomHeaderProps } from "ag-grid-react";
import { Modal } from "components";
import { AgGrid, AgGridRef, ZColDef } from "components/AgGrid/AgGrid";
import { ZButton } from "components/ZButton";
import { ZCheckboxButton } from "components/ZCheckboxButton";
import { ZIcon } from "components/ZIcon";
import { ZTooltip } from "components/ZTooltip";
import { ZIconType } from "helpers/utility/iconMap";
import { HasAllValues } from "helpers/utility/types";
import { MergeRequest, MergeValueKeep } from "models/daoModels/managedStock/MergeRequest";
import { ManagedDepartment } from "models/dbModels/managedDepartments/ManagedDepartment";
import { ManagedManufacturer } from "models/dbModels/managedManufacturers/ManagedManufacturer";
import { ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { ManagedStockGroup } from "models/dbModels/managedStockGroups/ManagedStockGroup";
import { ManagedSubdepartment } from "models/dbModels/managedSubdepartments/ManagedSubdepartment";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { useEffect, useRef, useState } from "react";
import { Alert, Modal as BSModal, Col, Row } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getDepartments } from "redux/slices/Departments";
import { getManufacturers } from "redux/slices/ManagedManufacturers";
import { getStockGroups } from "redux/slices/StockGroups";
import { getStocks, mergeStocks } from "redux/slices/Stocks";
import { getSubDepartments } from "redux/slices/SubDepartment";
import { getSuppliers } from "redux/slices/Suppliers";
import { showToast } from "redux/slices/Toasts";

type ManagedStockMergeRowData = {
    stockProp: keyof ManagedStockView;
    mergedStockPropPreview?: string; // The id of the stock to take the value from
    [key: string]: ManagedStockView[keyof ManagedStockView] | undefined; // Each row will only have a single property of ManagedStockView for each managed stock
}

type MergePreview = {
    [key in keyof ManagedStockView]?: string;
}

const hasAllStockPropKeys = HasAllValues<keyof ManagedStockView>();

const stockPropKeys: (keyof ManagedStockView)[] = hasAllStockPropKeys(
    [
        "name",
        "supplierId",
        "departmentId", 
        "subdepartmentId",
        "stockGroupIds",
        "minOrderQty",
        "cartonSize", 
        "minStockLevel",
        "maxStockLevel",
        "costEx", 
        "gstCode",
        "baseSellPrice", 
        "rrp",
        "active", 
        "ethical",
        "additionalOrderInformation", 
        "manufacturerId",
        // Skip
        "stockId",
        "dispenseName",
        "schedule",
        "storeGroupId",
        "apns", 
        "pdes",
        "productNo",
        "isPbs",
        "locked",
        "id",
    ]);

const propSkipList: (keyof ManagedStockView)[] = [
    "apns",
    "pdes",
    "locked",
    "storeGroupId",
    "id",
    "stockId",
    "productNo",
    "isPbs",
    "dispenseName",
    "schedule",
]

const filteredProps = stockPropKeys.filter(k => propSkipList.indexOf(k) === -1);

export type ManagedStockMergeProps = {
    stockIds: number[];
    loadStocks: boolean; // TODO: THIS IS TEMPORARY TO GET AROUND ANNOYING REDUX LOOPS
    show?: boolean;
    afterMerge?: (mergeStockId: number) => void;
    handleClose?: () => void; // Callback for handling the closure of the merge popup
}

interface MergeHeaderProps extends CustomHeaderProps {
    mainStockId: number | null;
    id: number;
    isSystem?: boolean;
    systemStockExists?: boolean;
    onClick: () => void;
}


export function ManagedStockMerge({ stockIds, show, loadStocks, afterMerge, handleClose }: ManagedStockMergeProps) {
    const gridRef = useRef<AgGridRef>(null);
    // const [mergePreview, setMergePreview] = useState<MergePreview>({});
    const [stocks, setStocks] = useState<ManagedStockView[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [mergePreview, setMergePreview] = useState<MergePreview>({});
    const stockStore = useAppSelector(s => s.stocks);
    const dispatch = useAppDispatch();
    const suppliers = useAppSelector(s => s.suppliers);
    const departments = useAppSelector(s => s.department);
    const subDepartments = useAppSelector(s => s.subDepartment);
    const manufacturers = useAppSelector(s => s.managedManufacturers);
    const stockGroups = useAppSelector(s => s.stockGroups);
    const [mergeRowData, setMergeRowData] = useState<ManagedStockMergeRowData[]>([]);
    const [showMergeConfirmation, setShowMergeConfirmation] = useState<boolean>(false);
    const [mergeError, setMergeError] = useState<string | null>(null);
    const [merging, setMerging] = useState<boolean>(false);
    const [mainStockId, setMainStockId] = useState<number | null>(null);
    const defaultMergeColDefs: ZColDef<ManagedStockMergeRowData>[] = [
        {
            headerName: "",
            field: "stockProp",
            pinned: true,
            minWidth: 100,
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<ManagedStockMergeRowData>) => {
                if (params.data) {
                    // Convert the actual prop name to a nicer user-friendly format (eg. name => Name, DepartmentId => Department, etc.)
                    let result = params.data.stockProp.replace(/([A-Z])/g, ' $1').replace(/\sId([s])*/, "$1");
                    result = (result.charAt(0).toUpperCase() + result.slice(1)).trim();
                    
                    switch(result) {
                        case "Gst Code":
                            result = "GST";
                            break;
                        case "Cost Ex":
                            result = "Cost";
                            break;
                        case "Rrp":
                            result = "RRP";
                            break;
                        default:
                            break;
                    }
                    return result;
                } else {
                    return "";
                }
            }
        },
        {
            headerName: "Merge Preview",
            field: "mergedStockPropPreview",
            minWidth: 300,
            pinned: true,
            valueFormatter: (params: ValueFormatterParams<ManagedStockMergeRowData>) => {
                if (params.data != null && params.value != null) {
                    const id = params.value;
                    const stockData = params.data[id];
                    // We only have the id stored on the stocks themselves, so we need to match these to the relevant data and grab the name of them to display
                    if (params.data.stockProp === "departmentId" && stockData != null) {
                        return departments.table.find((d: ManagedDepartment) => d.id === stockData)?.name ?? "";
                    } else if (params.data?.stockProp === "subdepartmentId" && stockData != null) {
                        return subDepartments.table.find((d: ManagedSubdepartment) => d.id === stockData)?.name ?? "";
                    } else if (params.data?.stockProp === "supplierId" && stockData != null) {
                        return suppliers.table.find((d: ManagedSupplier) => d.id === stockData)?.name ?? "";
                    } else if (params.data?.stockProp === "manufacturerId" && stockData != null) {
                        return manufacturers.table.find((d: ManagedManufacturer) => d.id === stockData)?.name ?? "";
                    } else if (params.data?.stockProp === "stockGroupIds" && stockData != null && stockData) {
                        return stockGroups.table.filter((sg: ManagedStockGroup) => (stockData as number[]).find(s => sg.id === s)).map((sg: ManagedStockGroup) => sg.name).join(", ");
                    } else if ((params.data?.stockProp === "active" || params.data.stockProp === "ethical") && stockData != null) {
                        return stockData === true ? "Yes" : "No";
                    }
                    return (params.data[params.value] ?? "").toString();
                }
                return "";
            },
            cellRendererSelector: (params: ICellRendererParams<ManagedStockMergeRowData>) => {
                const cellRendererParams = params.value == null ? {
                        align: "center", 
                        decorator: <ZTooltip text="No stock has been selected for this field"><ZIcon icon={ZIconType.Alert} variant="warning"/></ZTooltip>,
                        decoratorPosition: "start"
                } : {};
                if (params.data?.stockProp === "costEx" || params.data?.stockProp === "rrp" || params.data?.stockProp === "baseSellPrice") {
                    return { component: 'priceCellRenderer', params: cellRendererParams };
                } else {
                    return { component: 'baseCellRenderer', params: cellRendererParams };
                }
            }
        },
    ]

    const [mergeColDefs, setMergeColDefs] = useState<ZColDef<ManagedStockMergeRowData>[]>(defaultMergeColDefs);

    const CustomMergeHeader = ({ displayName, isSystem = false, systemStockExists = false, mainStockId, id, onClick }: MergeHeaderProps) => {
        return (
            <>
            <div className={`custom-header d-flex align-items-center ${systemStockExists ? "prevent-select disabled-div" : ""}`} onClick={onClick}>
                <ZIcon 
                    iconClassName="p-1 merge-header-icon"
                    icon={mainStockId === id ? ZIconType.Star : ZIconType.StarOutline}
                    variant="warning"
                />
                <span>{displayName}</span>
            </div>
            {isSystem && <ZTooltip text="This is a system stock"><ZIcon iconClassName="ms-1" icon={ZIconType.System}/></ZTooltip>}
            </>
        )
    };

    useEffect(() => {
        // Fetch stockIds that are passed to manual merge
        if (stockIds && stockIds.length > 0 && show) {
            setLoading(true);
            if (loadStocks || true) { //Fix for the optimised search which returns a reduced subset of data
                dispatch(getStocks({ids: stockIds})).then(res => {
                    const stockList = res.data;
                    // If there's a system stock, put it at the front
                    const sortedStocks = sortByProductNo(stockList);
                     if (sortedStocks.length > 0) {
                        setMainStockId(sortedStocks[0].id);
                     }
                    setStocks(sortedStocks);
                    setGridData(sortedStocks);
                    setMergeColDefs(getNewColumns(sortedStocks));
                });
            } else {
                const stockList = stockStore.table.filter(s => stockIds.findIndex(id => id === s.id) !== -1) as ManagedStockView[];
                // If there's a system stock, put it at the front
                const sortedStocks = sortByProductNo(stockList);
                if (sortedStocks.length > 0) {
                    setMainStockId(sortedStocks[0].id);
                }
                setStocks(sortedStocks);
                setGridData(sortedStocks);
                setMergeColDefs(getNewColumns(sortedStocks));
            }
        } else {
            setGridData([]);
            setMainStockId(null);
        }
        
    }, [show]);

    useEffect(() => {
        dispatch(getSuppliers());
        dispatch(getDepartments());
        dispatch(getSubDepartments());
        dispatch(getManufacturers());
        dispatch(getStockGroups());
    }, []);

    function sortByProductNo(stockList: ManagedStockView[]): ManagedStockView[] {
        return [...stockList].sort((a, b) => {
            if (a.productNo != null) {
             return -1;
            } else if (b.productNo != null) {
             return 1;
            }

            return 0;
         });
    }

    function setGridData(stocks: ManagedStockView[]) {
        // We need to set up rows for each of the props we want to show and set the columns to be the prop from each of the stocks we fetched
        // The row will have the structure:
        // [prop (eg. "name")], [Merge Preview of prop (ie the value of the selected stock, PANADOL or Panadol)], [stock_1.prop (eg. "PANADOL")], [stock_2.prop (eg. "Panadol")]
        const rowData = [...filteredProps.map<ManagedStockMergeRowData>((k: keyof ManagedStockView) => {
            const entries = stocks.reduce<{[id: string]: ManagedStockView[keyof ManagedStockView]}>((a, x) => {
                return {
                    ...a,
                    [x.id]: x[k]
                }
            }, {});

            const rowDataEntry: ManagedStockMergeRowData = {
                stockProp: k,
                mergedStockPropPreview: undefined,
                ...entries
            }
            return rowDataEntry;
        })];

        gridRef.current?.setGridOption("rowData", rowData);

        setMergeRowData(rowData);
    }

    /**
     * This method allows us to just update the info we want (mainStockId) on the column header.
     * This way we don't have to completely redefine the column definitions which should be more efficient.
     * The reason we have to do this is AgGrid's custom headers don't seem to play too nicely with reactive state,
     * and so even if the header component was listening to the mainStockId state, it would have stale data from whatever
     * mainStockId was set to when the column definitions were created. This means we *have* to modify the definitions whenever
     * mainStockId changes, and this seems like the best way.
     */
    function updateColumnHeaders() {
        const newDefs = mergeColDefs.map(def => {
            if (!isNaN(Number.parseInt(def.field ?? ""))) {
                // We are looking at the stock columns since the column field was parsable to an int
                return {
                    ...def,
                    headerComponentParams: {
                        ...def.headerComponentParams,
                        mainStockId: mainStockId
                    }
                }
            }

            return def;
        });

        gridRef.current?.setGridOption("columnDefs", newDefs);
    }

    function getNewColumns(stocks: ManagedStockView[]): ZColDef<ManagedStockMergeRowData>[] {
        const sortedStocks = sortByProductNo(stocks);

        const stockColDefs = sortedStocks.map<ZColDef<ManagedStockMergeRowData>>((s, ix) => {
            return {
                headerName: "Stock #" + (ix + 1),
                minWidth: 100,
                headerComponent: CustomMergeHeader,
                headerComponentParams: {
                    mainStockId: mainStockId,
                    id: s.id,
                    isSystem: s.productNo != null,
                    systemStockExists: sortedStocks?.some(s => s.productNo != null),
                    onClick: () => {
                        setMainStockId(s.id);
                    }
                },
                field: s.id.toString(),
                onCellClicked: (params: CellClickedEvent<ManagedStockMergeRowData>) => {
                    // let newPreview: MergePreview = {...mergePreview};
                    // console.log("starting newPreview: ", newPreview);
                    if (params.data?.stockProp != null) {
                        if (params.data.stockProp === "departmentId") {
                            // Set subdepartmentId too
                            const subDeptNode = params.api.getRowNode(`subdepartmentId`);
                            if (subDeptNode && subDeptNode.data) {
                                subDeptNode.setData({
                                    ...subDeptNode.data,
                                    mergedStockPropPreview: s.id.toString()
                                });
                                
                                // newPreview = {
                                //     ...newPreview,
                                //     [subDeptNode.data.stockProp]: s.id.toString()
                                // }
                            }
                        } else if (params.data.stockProp === "subdepartmentId") {
                            // Set departmentId too
                            const deptNode = params.api.getRowNode(`departmentId`);
                            if (deptNode && deptNode.data) {
                                deptNode.setData({
                                    ...deptNode.data,
                                    mergedStockPropPreview: s.id.toString()
                                });
                                
                                // newPreview = {
                                //     ...newPreview,
                                //     [deptNode.data.stockProp]: s.id.toString()
                                // }
                            }
                        }

                        params.node.setData({
                            ...params.data,
                            mergedStockPropPreview: s.id.toString()
                        })

                        // setMergePreview({
                        //     ...newPreview,
                        //     [params.data!.stockProp]: s.id.toString()
                        // });
                        checkMergeErrors();
                    }
                },
                valueFormatter: (params: ValueFormatterParams<ManagedStockMergeRowData>) => {
                    // Need to map ids to names for:
                    /** 
                     * - Supplier
                     * - Department
                     * - Subdepartment
                     * - Manufacturer
                     */
                    if (params.data != null) {
                        const stockData = params.data[s.id.toString()];
                        if (params.data.stockProp === "departmentId" && stockData != null) {
                            return departments.table.find((d: ManagedDepartment) => d.id === stockData)?.name ?? "";
                        } else if (params.data?.stockProp === "subdepartmentId" && stockData != null) {
                            return subDepartments.table.find((d: ManagedSubdepartment) => d.id === stockData)?.name ?? "";
                        } else if (params.data?.stockProp === "supplierId" && stockData != null) {
                            return suppliers.table.find((d: ManagedSupplier) => d.id === stockData)?.name ?? "";
                        } else if (params.data?.stockProp === "manufacturerId" && stockData != null) {
                            return manufacturers.table.find((d: ManagedManufacturer) => d.id === stockData)?.name ?? "";
                        } else if (params.data?.stockProp === "stockGroupIds" && stockData != null && stockData) {
                            return stockGroups.table.filter((sg: ManagedStockGroup) => (stockData as number[]).find(s => sg.id === s)).map((sg: ManagedStockGroup) => sg.name).join(", ");
                        } else if ((params.data?.stockProp === "active" || params.data.stockProp === "ethical") && stockData != null) {
                            // Change boolean column to Yes/No
                            return stockData === true ? "Yes" : "No";
                        }
                        return (params.data[s.id.toString()] ?? "").toString();
                    }
                    return "";
                },
                cellRendererSelector: (params: ICellRendererParams<ManagedStockMergeRowData>) => {
                    // Add checkbox to the stock columns
                    const cellRendererParams = {
                        decorator: <ZCheckboxButton className="me-1" name={params.data?.stockProp} checked={(params.data?.mergedStockPropPreview === s.id.toString())} type="radio" />,
                        decoratorPosition: "start"
                    }
                    // Certain rows need to be rendered differently (price rows)
                    if (params.data?.stockProp === "costEx" || params.data?.stockProp === "rrp" || params.data?.stockProp === "baseSellPrice") {
                        return { component: 'priceCellRenderer', params: cellRendererParams };
                    } else {
                        return { component: 'baseCellRenderer', params: cellRendererParams };
                    }
                }, 
            }
        });

        // Then update column definitions so we can have a custom stock in each column (on top of the existing default column defs)
        const newColDefs = defaultMergeColDefs.concat(stockColDefs);

        setLoading(false);
        return newColDefs;
    }
    
    // useEffect(() => {
    //     if (mergePreview && filteredProps.some((k: keyof ManagedStockView) => typeof mergePreview[k] !== "string")) {
    //         setMergeError("A stock must be selected for each field before you can merge!");
    //     } else {
    //         setMergeError(null);
    //     }
    // }, [mergePreview]);

    function getPreviewFromRowData(): MergePreview {
        const nodes = gridRef.current?.getAllRowNodes();
        console.log("Nodes: ", nodes);
        const preview = nodes?.reduce<MergePreview>((a, x) => {
            let rowData = x.data as ManagedStockMergeRowData;
            return {
                ...a,
                [rowData.stockProp]: rowData.mergedStockPropPreview
            }
        }, {}) ?? {};

        console.log("getPreviewFromRowData = ", preview);

        return preview;
    }

    function checkMergeErrors() {
        let preview = getPreviewFromRowData();
        if (preview && filteredProps.some((k: keyof ManagedStockView) => typeof preview[k] !== "string")) {
            setMergeError("A stock must be selected for each field before you can merge!");
        } else {
            setMergeError(null);
        }
    }

    useEffect(() => {
        if (mainStockId != null) {
            updateColumnHeaders();
            setMergePreviewToMainStock();
        }
    }, [mainStockId]);

    function setMergePreviewToMainStock() {
        let newPreview: MergePreview = {};
        const rowNodes = gridRef.current?.getAllRowNodes();
            rowNodes?.forEach(node => {
                gridRef.current?.setCellData(node, "mergedStockPropPreview", mainStockId?.toString());
                newPreview = {
                    ...newPreview,
                    [node.data.stockProp]: mainStockId?.toString()
                }
        });
        checkMergeErrors();
    }

    function performStockMerge() {
        console.log("Merging: ", mergePreview);
        if (stocks.length === 0) return; // Shouldn't ever happen but just in case
        setMerging(true);
        // The backend accepts merge requests which handle only 2 stocks at a time so we will need to send multiple merge requests
        // and perform some logic to figure out how to merge the data properly

        // I think if we have stocks A, B, C, D, we need 3 requests for (A,B), (A,C) and (A,D)
        let mergeRequests: MergeRequest[] = [];
        const mainStock = stocks.find(s => s.id === mainStockId) ?? stocks[0];
        stocks.forEach(s => {
            if (s === mainStock) return;

            function TakeFromStock(stockId: number, key: keyof ManagedStockView): boolean {
                return mergePreview[key] === stockId.toString();
            }
            
            const mergeReq: MergeRequest = {
                keepId: mainStock.id,
                deleteId: s.id,
                name: TakeFromStock(s.id, "name") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                departmentId: TakeFromStock(s.id, "departmentId") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                subdepartmentId: TakeFromStock(s.id, "subdepartmentId") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                manufacturerId: TakeFromStock(s.id, "manufacturerId") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                supplierId: TakeFromStock(s.id, "supplierId") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                cartonSize: TakeFromStock(s.id, "cartonSize") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                minOrderQty: TakeFromStock(s.id, "minOrderQty") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                minStockLevel: TakeFromStock(s.id, "minStockLevel") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                maxStockLevel: TakeFromStock(s.id, "maxStockLevel") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                gstCode: TakeFromStock(s.id, "gstCode") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                active: TakeFromStock(s.id, "active") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                ethical: TakeFromStock(s.id, "ethical") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                locked: TakeFromStock(s.id, "locked") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                costEx: TakeFromStock(s.id, "costEx") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                baseSellPrice: TakeFromStock(s.id, "baseSellPrice") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                rRP: TakeFromStock(s.id, "rrp") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                additionalOrderInformation: TakeFromStock(s.id, "additionalOrderInformation") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
                stockGroupIds: TakeFromStock(s.id, "stockGroupIds") ? MergeValueKeep.Delete : MergeValueKeep.Keep,
            }

            mergeRequests.push(mergeReq); 
        });

        // Actually dispatch the merge call
        dispatch(mergeStocks(mergeRequests)).then(() => {
            // Now we have our list of merge reqs, send them to the backend
            setMerging(false);
            setShowMergeConfirmation(false);
            afterMerge && afterMerge(mainStock.id);
        }).catch(() => {
            setMerging(false);
            dispatch(showToast({value: ["An error occurred while merging the stocks."]}));
        })
    }

    return (
        <>
             <Modal
                title="Confirm - Manual Merge"
                show={showMergeConfirmation}
                buttonType="primary"
                buttonText={"Merge"}
                size="sm"
                handleClose={() => setShowMergeConfirmation(false)}
                handleSubmit={() => performStockMerge()}
                disableButtons={merging}
                description={
                    <>
                        <p>Are you sure you want to continue with the merge?</p>
                        <Alert variant="warning" className="d-flex align-items-center"><ZIcon icon={ZIconType.Alert} variant="warning" size="14px" iconClassName="pe-1"/>Note: This process is irreversible!</Alert>
                    </>
                }
            />
            <>
            {!showMergeConfirmation && 
                <BSModal size="xl" show={show} centered
                    contentClassName="modal-full-height"
                    keyboard
                    animation
                    backdrop
                    onHide={handleClose}
                    onEscapeKeyDown={handleClose}
                >
                    <BSModal.Header closeButton>
                        <BSModal.Title>Manual Merge</BSModal.Title>
                    </BSModal.Header>
                    <BSModal.Body className="d-flex flex-column">
                        <AgGrid
                            getRowId={(p) => `${p.data.stockProp}` }
                            ref={gridRef}
                            // rowSelectionType="multiple"
                            // onSelectionChanged={(s) => {
                            //     setRowsToLink(s as ManagedStockConflict[]);
                            // }}
                            rowData={mergeRowData}
                            columnDefs={mergeColDefs}
                            // searchText={searchText}
                            loading={loading}
                            // isRowSelectable={(row: IRowNode<ConflictRowData>) => row.data?.conflict != null}
                        />
                    </BSModal.Body>
                    <BSModal.Footer>
                        <Row>
                            <Col xs={12} className="d-flex flex-row-reverse gap-2">
                                <ZTooltip enabled={mergeError != null} text={mergeError ?? "Unknown error"}>
                                    <ZButton
                                        buttonText={merging ? "Merging..." : "Merge"}
                                        loading={merging}
                                        variant="primary"
                                        disabled={mergeError != null}
                                        className="cta-button"
                                        onClick={() => {
                                            setShowMergeConfirmation(true);
                                            setMergePreview(getPreviewFromRowData());
                                        }}
                                    />
                                </ZTooltip>
                                <ZButton
                                    variant="light"
                                    buttonText="Cancel"
                                    className="cta-button"
                                    onClick={handleClose}
                                />
                            </Col>
                        </Row>
                    </BSModal.Footer>
                </BSModal>
            }
            </>
        </>
    );
}